import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CurrentUser } from '../../domain/entities/User';
import { UserRole } from '../../domain/value-objects/UserRole';
import { LoginCommand } from '../dto/auth/LoginCommand';
import { RegisterUserCommand } from '../dto/auth/RegisterUserCommand';
import { ForgotPasswordCommand } from '../dto/auth/ForgotPasswordCommand';
import { ResetPasswordCommand } from '../dto/auth/ResetPasswordCommand';
import { RegisterResult } from '../dto/auth/LoginResult';
import { AuthService } from '../../infrastructure/auth/AuthService';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { ApiError } from '../../infrastructure/api/ApiError';
import { notificationService } from '../../infrastructure/notifications';
import { extractBusinessId } from '../../infrastructure/auth/jwtUtils';

/**
 * Password reset workflow state
 */
export type PasswordResetState = 'idle' | 'requesting' | 'emailSent' | 'resetting' | 'success' | 'error';

/**
 * Account lockout state
 */
export interface AccountLockout {
  isLocked: boolean;
  lockoutExpiresAt: string | null; // ISO datetime
  remainingAttempts?: number;
}

/**
 * Auth state interface
 */
interface AuthState {
  // Core authentication state
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loadingMessage: string | null; // SESSION_228: Context-aware loading message
  error: string | null;

  // Token management
  tokenExpiresAt: string | null; // ISO datetime when access token expires
  refreshIntervalId: number | null; // setInterval ID for proactive refresh

  // Session timeout warning
  sessionWarningShown: boolean;

  // Password reset state
  passwordResetState: PasswordResetState;
  passwordResetEmail: string | null;
  passwordResetError: string | null;

  // Account lockout state
  accountLockout: AccountLockout;

  // Core auth actions
  login: (command: LoginCommand) => Promise<void>;
  register: (command: RegisterUserCommand) => Promise<RegisterResult | undefined>;
  logout: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  clearError: () => void;

  // Token management actions
  startTokenRefreshTimer: () => void;
  stopTokenRefreshTimer: () => void;
  refreshAccessToken: () => Promise<void>;

  // Session management actions
  showSessionWarning: () => void;
  hideSessionWarning: () => void;
  extendSession: () => Promise<void>;

  // Password reset actions
  forgotPassword: (command: ForgotPasswordCommand) => Promise<void>;
  resetPassword: (command: ResetPasswordCommand) => Promise<void>;
  clearPasswordResetState: () => void;

  // Account lockout actions
  setAccountLockout: (lockout: AccountLockout) => void;
  clearAccountLockout: () => void;
}

/**
 * Create auth service instance
 */
const authService = new AuthService(apiClient);

/**
 * Constants for token refresh timing
 */
const TOKEN_REFRESH_CHECK_INTERVAL = 60000; // Check every 1 minute
const TOKEN_REFRESH_THRESHOLD = 0.8; // Refresh at 80% of token lifetime
const SESSION_WARNING_MINUTES = 5; // Warn user 5 minutes before expiry

/**
 * Helper: Calculate milliseconds until token expires
 */
function getMillisecondsUntilExpiry(expiryTime: string): number {
  return new Date(expiryTime).getTime() - Date.now();
}

/**
 * Helper: Check if token should be refreshed (at 80% of lifetime)
 */
function shouldRefreshToken(expiryTime: string | null): boolean {
  if (!expiryTime) {return false;}

  const msUntilExpiry = getMillisecondsUntilExpiry(expiryTime);
  if (msUntilExpiry <= 0) {return true;} // Already expired

  // Typical token lifetime is 1 hour (3600000 ms)
  // Refresh at 80% = 48 minutes remaining
  const refreshThreshold = 3600000 * TOKEN_REFRESH_THRESHOLD;
  return msUntilExpiry < (3600000 - refreshThreshold);
}

/**
 * Helper: Check if session warning should be shown (5 minutes before expiry)
 */
function shouldShowSessionWarning(expiryTime: string | null): boolean {
  if (!expiryTime) {return false;}

  const msUntilExpiry = getMillisecondsUntilExpiry(expiryTime);
  const warningThreshold = SESSION_WARNING_MINUTES * 60 * 1000;

  return msUntilExpiry > 0 && msUntilExpiry <= warningThreshold;
}

/**
 * Module-level promise for deduplicating concurrent refresh requests.
 * Prevents the proactive timer and 401 interceptor from racing and
 * consuming the same single-use refresh token twice.
 */
let activeRefreshPromise: Promise<void> | null = null;

/**
 * Auth Store - Manages authentication state
 *
 * Features:
 * - User information and authentication status
 * - Login/logout/register operations
 * - Automatic token refresh
 * - Session timeout detection
 * - Password reset workflows
 * - Account lockout handling
 * - State persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      loadingMessage: null,
      error: null,

      // Token management
      tokenExpiresAt: null,
      refreshIntervalId: null,

      // Session timeout
      sessionWarningShown: false,

      // Password reset
      passwordResetState: 'idle',
      passwordResetEmail: null,
      passwordResetError: null,

      // Account lockout
      accountLockout: {
        isLocked: false,
        lockoutExpiresAt: null,
        remainingAttempts: undefined
      },

      // Login action
      login: async (command: LoginCommand): Promise<void> => {
        // Clear any existing lockout state before login attempt
        get().clearAccountLockout();

        set({ isLoading: true, loadingMessage: 'Signing in...', error: null });

        try {
          const result = await authService.login(command);

          if (!result.succeeded) {
            const errorMsg = result.errorMessage || 'Login failed';

            // Check for account lockout errors
            if (errorMsg.toLowerCase().includes('locked') || errorMsg.toLowerCase().includes('lockout')) {
              // Extract lockout expiry if available (backend should provide this)
              const lockoutExpiresAt: string | null = result.errors && result.errors.length > 0
                ? result.errors[0] ?? null // Backend may send ISO datetime in errors array
                : null;

              set({
                accountLockout: {
                  isLocked: true,
                  lockoutExpiresAt,
                  remainingAttempts: 0
                }
              });
            }

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              loadingMessage: null,
              error: errorMsg,
              tokenExpiresAt: null
            });

            throw new Error(errorMsg);
          }

          // Extract businessId from JWT token claims
          const businessId = extractBusinessId(result.accessToken);

          // SESSION_204: Compute display name from firstName + lastName, fallback to email
          const firstName = result.firstName ?? null;
          const lastName = result.lastName ?? null;
          const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
          const displayName = fullName || result.email;

          // Map login result to CurrentUser
          // SESSION_189: Include emailConfirmed for route guard
          // SESSION_204: Include firstName, lastName, and computed displayName
          const user: CurrentUser = {
            id: result.userId,
            email: result.email,
            userName: result.userName,
            firstName,
            lastName,
            displayName,
            role: (result.roles?.[0] as 'Admin' | 'BusinessOwner') ?? 'BusinessOwner',
            businessId: businessId, // Extract from JWT token, not hardcoded
            emailConfirmed: result.emailConfirmed ?? false // SESSION_189: Email confirmation status
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: result.tokenExpiryTime, // Store token expiry
            accountLockout: {
              isLocked: false,
              lockoutExpiresAt: null,
              remainingAttempts: undefined
            }
          });

          // Start automatic token refresh timer
          get().startTokenRefreshTimer();

          // SESSION_204: Use first name if available for friendlier greeting
          const greeting = firstName || user.displayName || user.email;
          notificationService.showSuccess(`Welcome back, ${greeting}!`, 'Login Successful');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: errorMessage
          });

          throw error;
        }
      },

      // Register action
      // SESSION_187: Updated to return RegisterResult for 3D Secure handling
      register: async (command: RegisterUserCommand): Promise<RegisterResult | undefined> => {
        set({ isLoading: true, loadingMessage: 'Creating your account...', error: null });

        try {
          // Cast result to RegisterResult to include subscription fields
          const result = await authService.register(command) as RegisterResult;

          if (!result.succeeded) {
            const errorMsg = result.errorMessage || 'Registration failed';
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              loadingMessage: null,
              error: errorMsg,
              tokenExpiresAt: null
            });
            throw new Error(errorMsg);
          }

          // Extract businessId from JWT token claims
          const businessId = extractBusinessId(result.accessToken);

          // SESSION_204: Compute display name from firstName + lastName, fallback to email
          const firstName = result.firstName ?? null;
          const lastName = result.lastName ?? null;
          const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
          const displayName = fullName || result.email;

          // Map registration result to CurrentUser
          // SESSION_189: Include emailConfirmed (false for new registrations)
          // SESSION_204: Include firstName, lastName, and computed displayName
          const user: CurrentUser = {
            id: result.userId,
            email: result.email,
            userName: result.userName,
            firstName,
            lastName,
            displayName,
            role: (result.roles?.[0] as 'Admin' | 'BusinessOwner') ?? 'BusinessOwner',
            businessId: businessId, // Extract from JWT token, not hardcoded
            emailConfirmed: result.emailConfirmed ?? false // SESSION_189: Always false for new registrations
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: result.tokenExpiryTime // Store token expiry
          });

          // Start automatic token refresh timer
          get().startTokenRefreshTimer();

          // SESSION_204: Use first name if available for friendlier greeting
          const greeting = firstName || user.displayName || user.email;
          notificationService.showSuccess(`Welcome, ${greeting}! Your account has been created.`, 'Registration Successful');

          // SESSION_187: Return result for 3D Secure handling in RegisterPage
          return result;
        } catch (error) {
          // Extract detailed error message from ApiError
          let errorMessage = 'Registration failed';

          if (ApiError.isApiError(error)) {
            // Get all error messages from the API response
            const allMessages = error.getAllErrorMessages();
            // Use the first specific error message, or the main message
            const firstMessage = allMessages[0];
            if (firstMessage !== undefined) {
              errorMessage = firstMessage;
            } else {
              errorMessage = error.message;
            }

            // If we have validation errors, format them nicely
            if (error.errors) {
              const validationMessages = Object.values(error.errors).flat();
              const firstValidation = validationMessages[0];
              if (firstValidation !== undefined) {
                errorMessage = firstValidation;
              }
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }

          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: errorMessage
          });

          throw error;
        }
      },

      // Logout action
      logout: async (): Promise<void> => {
        set({ isLoading: true, loadingMessage: 'Signing out...' });

        // Stop token refresh timer
        get().stopTokenRefreshTimer();

        try {
          await authService.logout();

          notificationService.showInfo('You have been logged out successfully', 'Logged Out');
        } catch (error) {
          // Even if logout API call fails, we still clear local state
        } finally {
          // Always clear state, even if API call fails
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: null,
            sessionWarningShown: false,
            passwordResetState: 'idle',
            passwordResetEmail: null,
            passwordResetError: null,
            accountLockout: {
              isLocked: false,
              lockoutExpiresAt: null,
              remainingAttempts: undefined
            }
          });

          // Redirect to home page after logout
          window.location.href = '/';
        }
      },

      // Load current user (e.g., on app initialization)
      loadCurrentUser: async (): Promise<void> => {
        // Check if token exists
        if (!authService.isAuthenticated()) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null
          });
          return;
        }

        set({ isLoading: true, loadingMessage: 'Checking authentication...', error: null });

        try {
          const user = await authService.getCurrentUser();

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            loadingMessage: null,
            error: null
          });

          // Start token refresh timer if user is authenticated
          get().startTokenRefreshTimer();
        } catch (error) {
          // Token is invalid or expired
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            loadingMessage: null,
            error: null,
            tokenExpiresAt: null
          });
        }
      },

      // Clear error
      clearError: (): void => {
        set({ error: null });
      },

      // ===== TOKEN MANAGEMENT ACTIONS =====

      /**
       * Start automatic token refresh timer
       * Checks every minute if token needs refresh (at 80% of lifetime)
       */
      startTokenRefreshTimer: (): void => {
        const state = get();

        // Don't start if already running
        if (state.refreshIntervalId !== null) {
          return;
        }

        const intervalId = window.setInterval(() => {
          const currentState = get();

          // Stop if user logged out
          if (!currentState.isAuthenticated || !currentState.tokenExpiresAt) {
            get().stopTokenRefreshTimer();
            return;
          }

          // Check if token should be refreshed
          if (shouldRefreshToken(currentState.tokenExpiresAt)) {
            get().refreshAccessToken().catch((error) => {
              console.error('Auto-refresh failed:', error);
              // Token refresh failed - user will be logged out by ApiClient
            });
          }

          // Check if session warning should be shown
          if (shouldShowSessionWarning(currentState.tokenExpiresAt) && !currentState.sessionWarningShown) {
            get().showSessionWarning();
          }
        }, TOKEN_REFRESH_CHECK_INTERVAL);

        set({ refreshIntervalId: intervalId });
      },

      /**
       * Stop automatic token refresh timer
       */
      stopTokenRefreshTimer: (): void => {
        const state = get();

        if (state.refreshIntervalId !== null) {
          window.clearInterval(state.refreshIntervalId);
          set({ refreshIntervalId: null });
        }
      },

      /**
       * Manually refresh access token.
       * Uses module-level promise deduplication so the proactive timer
       * and the ApiClient 401 interceptor never race each other.
       */
      refreshAccessToken: async (): Promise<void> => {
        if (activeRefreshPromise) {
          return activeRefreshPromise;
        }

        activeRefreshPromise = (async () => {
          try {
            const currentToken = localStorage.getItem('authToken') || '';

            if (!currentToken) {
              throw new Error('No token to refresh');
            }

            const result = await authService.refreshToken(currentToken);

            if (!result.succeeded) {
              throw new Error(result.errorMessage || 'Token refresh failed');
            }

            // Update token expiry
            set({
              tokenExpiresAt: result.tokenExpiryTime,
              sessionWarningShown: false // Reset warning if session extended
            });
          } catch (error) {
            // Token refresh failed - user will be redirected to login by ApiClient
            console.error('Token refresh failed:', error);
            throw error;
          }
        })();

        try {
          await activeRefreshPromise;
        } finally {
          activeRefreshPromise = null;
        }
      },

      // ===== SESSION MANAGEMENT ACTIONS =====

      /**
       * Show session timeout warning modal
       */
      showSessionWarning: (): void => {
        set({ sessionWarningShown: true });
      },

      /**
       * Hide session timeout warning modal
       */
      hideSessionWarning: (): void => {
        set({ sessionWarningShown: false });
      },

      /**
       * Extend session by refreshing token
       */
      extendSession: async (): Promise<void> => {
        try {
          await get().refreshAccessToken();
          get().hideSessionWarning();
          notificationService.showSuccess('Your session has been extended', 'Session Extended');
        } catch (error) {
          notificationService.showError('Failed to extend session. Please log in again.');
          throw error;
        }
      },

      // ===== PASSWORD RESET ACTIONS =====

      /**
       * Request password reset email
       */
      forgotPassword: async (command: ForgotPasswordCommand): Promise<void> => {
        set({ passwordResetState: 'requesting', passwordResetError: null });

        try {
          const result = await authService.forgotPassword(command);

          if (!result.succeeded) {
            set({
              passwordResetState: 'error',
              passwordResetError: result.errorMessage || 'Failed to send reset email'
            });
            throw new Error(result.errorMessage || 'Failed to send reset email');
          }

          set({
            passwordResetState: 'emailSent',
            passwordResetEmail: command.email,
            passwordResetError: null
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';

          set({
            passwordResetState: 'error',
            passwordResetError: errorMessage
          });

          throw error;
        }
      },

      /**
       * Reset password with token
       */
      resetPassword: async (command: ResetPasswordCommand): Promise<void> => {
        set({ passwordResetState: 'resetting', passwordResetError: null });

        try {
          const result = await authService.resetPassword(command);

          if (!result.succeeded) {
            set({
              passwordResetState: 'error',
              passwordResetError: result.errorMessage || 'Failed to reset password'
            });
            throw new Error(result.errorMessage || 'Failed to reset password');
          }

          set({
            passwordResetState: 'success',
            passwordResetError: null
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';

          set({
            passwordResetState: 'error',
            passwordResetError: errorMessage
          });

          throw error;
        }
      },

      /**
       * Clear password reset state
       */
      clearPasswordResetState: (): void => {
        set({
          passwordResetState: 'idle',
          passwordResetEmail: null,
          passwordResetError: null
        });
      },

      // ===== ACCOUNT LOCKOUT ACTIONS =====

      /**
       * Set account lockout state
       */
      setAccountLockout: (lockout: AccountLockout): void => {
        set({ accountLockout: lockout });
      },

      /**
       * Clear account lockout state
       */
      clearAccountLockout: (): void => {
        set({
          accountLockout: {
            isLocked: false,
            lockoutExpiresAt: null,
            remainingAttempts: undefined
          }
        });
      }
    }),
    {
      name: 'auth-storage', // LocalStorage key
      partialize: (state) => ({
        // Only persist essential state - not loading/error states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        tokenExpiresAt: state.tokenExpiresAt
        // Don't persist: refreshIntervalId, sessionWarningShown, loading, error, password reset state
      })
    }
  )
);

/**
 * Selectors - Memoized state selectors for better performance
 */

/**
 * Get current user's role
 */
export const useUserRole = (): UserRole | null => {
  return useAuthStore((state) => state.user?.role ?? null);
};

/**
 * Check if user is admin
 */
export const useIsAdmin = (): boolean => {
  return useAuthStore((state) => state.user?.role === 'Admin');
};

/**
 * Check if user is business owner
 */
export const useIsBusinessOwner = (): boolean => {
  return useAuthStore((state) => state.user?.role === 'BusinessOwner');
};

/**
 * @deprecated Use useIsBusinessOwner instead
 * Kept for backward compatibility
 */
export const useIsCustomer = useIsBusinessOwner;

/**
 * Check if user is a non-profit user
 */
export const useIsNonProfitUser = (): boolean => {
  return useAuthStore((state) => state.user?.role === 'NonProfitUser');
};

/**
 * Get current user's business ID (for customers)
 */
export const useBusinessId = (): string | null => {
  return useAuthStore((state) => state.user?.businessId ?? null);
};

/**
 * Get authentication loading state
 */
export const useAuthLoading = (): boolean => {
  return useAuthStore((state) => state.isLoading);
};

/**
 * Get authentication error
 */
export const useAuthError = (): string | null => {
  return useAuthStore((state) => state.error);
};

/**
 * Get token expiry timestamp
 */
export const useTokenExpiresAt = (): string | null => {
  return useAuthStore((state) => state.tokenExpiresAt);
};

/**
 * Get session warning state
 */
export const useSessionWarning = (): boolean => {
  return useAuthStore((state) => state.sessionWarningShown);
};

/**
 * Get password reset state
 */
export const usePasswordResetState = (): PasswordResetState => {
  return useAuthStore((state) => state.passwordResetState);
};

/**
 * Get password reset email
 */
export const usePasswordResetEmail = (): string | null => {
  return useAuthStore((state) => state.passwordResetEmail);
};

/**
 * Get password reset error
 */
export const usePasswordResetError = (): string | null => {
  return useAuthStore((state) => state.passwordResetError);
};

/**
 * Get account lockout state
 */
export const useAccountLockout = (): AccountLockout => {
  return useAuthStore((state) => state.accountLockout);
};

/**
 * SESSION_189: Get email confirmation status
 */
export const useEmailConfirmed = (): boolean => {
  return useAuthStore((state) => state.user?.emailConfirmed ?? false);
};

/**
 * SESSION_189: Update email confirmation status
 * Used by EmailConfirmationPendingPage when "Try Again" is clicked
 */
export const updateEmailConfirmedStatus = (emailConfirmed: boolean): void => {
  useAuthStore.setState((state) => ({
    user: state.user ? { ...state.user, emailConfirmed } : null
  }));
};
